import React, { PropsWithChildren, ReactElement, RefObject, useEffect, useRef } from 'react';

import { useIsMobile, useModal } from '@src/utils/hooks';
import { bemCssModules, isObject, getTestId } from '@utils';

import { Input } from '@components/Input';
import NewModal from '@components/NewModal';

import SelectedIcon from './selected_icon.svg';
import useKeyboardNavigation from './useKeyboardNavigation';

import style from './Dropdown.scss';

const cn = bemCssModules(style, 'Dropdown');
const ti = getTestId('Dropdown');

type ClassNames = 'defaultOption' | 'label' | 'content' | 'group' | 'groupTitle';

export type TDropdown<DataObjectType> = {
  className?: string;
  classNames?: { [key in ClassNames]?: string };
  data?: DataObjectType[] | { [key: string]: DataObjectType[] };
  customLabel?: (props: DataObjectType) => React.ReactNode;
  customOption?: (props: DataObjectType) => React.ReactElement | null;
  value?: string | number;
  onChange?: (value: DataObjectType) => void;
  controller: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[];
  header?: React.ReactNode;
  disableKeyboardNavigation?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  isSearch?: boolean;
  search?: string;
  classNameSearch?: string;
  placeholderSearch?: string;
  inputId?: string;
  emptyText?: string;
  onSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type BaseDataObjectType = { value: string | number; label?: string } | string;

const Dropdown = <DataObjectType extends BaseDataObjectType>(
  props: PropsWithChildren<TDropdown<DataObjectType>>,
  outerRef: React.ForwardedRef<HTMLDivElement>
): ReactElement => {
  const {
    className,
    classNames,
    data,
    customLabel,
    customOption,
    value,
    onChange,
    controller,
    header,
    // TODO: fix keyboard navigation: hovered and focused conflict
    disableKeyboardNavigation = true,
    isOpen: forceIsOpen,
    onClose,
    isSearch,
    search,
    onSearch,
    classNameSearch,
    placeholderSearch,
    inputId,
    emptyText,
  } = props;

  const innerRef = useRef<HTMLDivElement | null>(null);
  const controllerRefs = Array.isArray(controller) ? controller : [controller];
  const [isOpen, setIsOpen] = useModal([innerRef, outerRef, ...controllerRefs]);
  const buttons = useKeyboardNavigation(disableKeyboardNavigation);

  const isMobile = useIsMobile();

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  const handleChange = (el: DataObjectType) => {
    onChange?.(el);
    handleClose();
  };

  useEffect(() => {
    const handleClick = () => setIsOpen(true);
    controllerRefs.forEach(ref => ref?.current?.addEventListener('click', handleClick));
    return () => {
      controllerRefs.forEach(ref => ref?.current?.removeEventListener('click', handleClick));
    };
  }, [controller]);

  useEffect(() => {
    if (typeof forceIsOpen === 'boolean') {
      if (!forceIsOpen) onClose?.();
    } else if (!isOpen) onClose?.();
  }, [isOpen, forceIsOpen]);

  const renderOptions = (optionsData: DataObjectType[]) =>
    optionsData.map((el, i) => {
      const key = typeof el === 'string' ? el : el.value;
      const label = typeof el === 'string' ? el : el.label || el.value;
      const component =
        typeof customOption !== 'undefined' ? (
          customOption?.(el)
        ) : (
          <button
            className={cn('default-option', ['text-base', classNames?.defaultOption])}
            type="button"
          >
            <div className={cn('label', [classNames?.label])}>{customLabel?.(el) || label}</div>
            {value && <SelectedIcon className={cn('selected-icon', { hidden: value !== key })} />}
          </button>
        );
      const onClick = e => {
        component?.props.onClick?.(e);
        handleChange(el);
      };
      const ref = (element: HTMLElement) => {
        buttons[i] = element;
      };
      return component ? React.cloneElement(component, { ref, key, onClick }) : null;
    });

  return (
    <NewModal
      className={cn(null, [className])}
      ref={node => {
        innerRef.current = node;
        if (typeof outerRef === 'function') outerRef(node);
        else if (outerRef) outerRef.current = node;
      }}
      isOpen={forceIsOpen ?? isOpen}
      onClose={handleClose}
      hideCloseButton
      overlay={false}
      notPortal
      disableScroll={isMobile}
    >
      {header}
      <div className={cn('content', [classNames?.content])}>
        {isSearch && (
          <Input
            withoutGradient
            className={cn('search', [classNameSearch])}
            value={search}
            onChange={onSearch}
            placeholder={placeholderSearch}
            id={inputId}
          />
        )}
        {Array.isArray(data) ? (
          renderOptions(data)
        ) : isObject(data) && Object.keys(data).length ? (
          Object.keys(data).map(
            name =>
              data[name].length > 0 && (
                <div className={cn('group', [classNames?.group])} key={name}>
                  <b
                    className={cn('group-title', ['small', classNames?.groupTitle])}
                    data-testid={ti('label')}
                  >
                    {name.replace(/_/g, ' ')}
                  </b>
                  {renderOptions(data[name])}
                </div>
              )
          )
        ) : emptyText ? (
          <div className={cn('empty')}>{emptyText}</div>
        ) : null}
      </div>
    </NewModal>
  );
};

declare module 'react' {
  function forwardRef<T, P = unknown>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export default React.forwardRef(Dropdown);
